import { RNG } from './RNG';
import {
    Card,
    Suit,
    Rank,
    HandType,
    HandResult,
    VideoPokerState,
    VideoPokerConfig,
    JACKS_OR_BETTER_PAYTABLE,
    QUALIFYING_PAIR_RANKS
} from '../types/poker.types';
import { getCurrentVariant, VideoPokerVariantConfig } from '../config/videopoker.config';
import { UserManager } from '../components/UserManager';

export class VideoPokerEngine {
    private rng: RNG;
    private state: VideoPokerState;
    private config: VideoPokerConfig;
    private variantConfig: VideoPokerVariantConfig;
    private deck: Card[];

    constructor(seed?: number) {
        this.rng = new RNG(seed);
        
        // Load current variant configuration
        this.variantConfig = getCurrentVariant();
        
        this.config = {
            minBet: 1,
            maxBet: 5,
            defaultBet: 1,
            paytable: this.variantConfig.paytable
        };

        this.state = {
            balance: 1000,
            bet: 1,
            hand: [],
            heldCards: [false, false, false, false, false],
            gamePhase: 'betting',
            lastWin: 0,
            sessionWins: 0,
            sessionLosses: 0
        };

        this.deck = this.createDeck();
    }

    private createDeck(): Card[] {
        const deck: Card[] = [];
        
        for (const suit of Object.values(Suit)) {
            for (let rank = 2; rank <= 14; rank++) {
                const id = this.getCardId(rank as Rank, suit);
                deck.push({
                    suit,
                    rank: rank as Rank,
                    id
                });
            }
        }
        
        return deck;
    }

    private getCardId(rank: Rank, suit: Suit): string {
        const rankChar = rank === 14 ? 'A' : 
                        rank === 13 ? 'K' :
                        rank === 12 ? 'Q' :
                        rank === 11 ? 'J' :
                        rank.toString();
        
        const suitChar = suit === Suit.HEARTS ? 'H' :
                        suit === Suit.DIAMONDS ? 'D' :
                        suit === Suit.CLUBS ? 'C' : 'S';
        
        return rankChar + suitChar;
    }

    private shuffleDeck(): void {
        this.deck = this.createDeck();
        for (let i = this.deck.length - 1; i > 0; i--) {
            const j = this.rng.randomInt(0, i);
            [this.deck[i], this.deck[j]] = [this.deck[j], this.deck[i]];
        }
    }

    public deal(): Card[] {
        if (this.state.gamePhase !== 'betting') return this.state.hand;

        // Deduct bet
        this.state.balance -= this.state.bet;
        this.state.sessionLosses += this.state.bet;

        // Sync balance with user manager
        this.syncBalance(0, this.state.bet);

        // Shuffle and deal 5 cards
        this.shuffleDeck();
        this.state.hand = this.deck.slice(0, 5);
        this.state.heldCards = [false, false, false, false, false];
        this.state.gamePhase = 'deal';


        return this.state.hand;
    }

    public dealForDisplay(): Card[] {
        // Deal cards for display only - no bet deduction, no state change
        this.shuffleDeck();
        return this.deck.slice(0, 5);
    }

    public holdCard(index: number): void {
        if (this.state.gamePhase !== 'deal' || index < 0 || index > 4) return;
        this.state.heldCards[index] = !this.state.heldCards[index];
    }

    public draw(): { hand: Card[], result: HandResult } {
        if (this.state.gamePhase !== 'deal') {
            return { hand: this.state.hand, result: this.evaluateHand(this.state.hand) };
        }

        // Replace non-held cards
        let deckIndex = 5; // Start after the initial 5 cards
        for (let i = 0; i < 5; i++) {
            if (!this.state.heldCards[i]) {
                this.state.hand[i] = this.deck[deckIndex++];
            }
        }

        this.state.gamePhase = 'evaluate';
        const result = this.evaluateHand(this.state.hand);
        
        // Calculate winnings
        const payout = result.payout;
        this.state.balance += payout;
        this.state.lastWin = payout;

        if (payout > 0) {
            this.state.sessionWins += payout;
            // Sync net profit (payout minus bet) with user manager
            const netProfit = payout - this.state.bet;
            this.syncBalance(netProfit, 0);
        }

        return { hand: this.state.hand, result };
    }

    // Public method to evaluate current hand without drawing
    public evaluateCurrentHand(): HandResult {
        if (this.state.hand.length === 0) {
            return {
                type: HandType.HIGH_CARD,
                payout: 0,
                description: ''
            };
        }
        return this.evaluateHand(this.state.hand);
    }

    public newGame(): void {
        this.state.gamePhase = 'betting';
        this.state.hand = [];
        this.state.heldCards = [false, false, false, false, false];
        this.state.lastWin = 0;
    }

    public setBet(amount: number): void {
        if (amount >= this.config.minBet && amount <= this.config.maxBet && this.state.gamePhase === 'betting') {
            this.state.bet = amount;
        }
    }

    private evaluateHand(hand: Card[]): HandResult {
        // Handle different variants
        if (this.variantConfig.specialRules.wildCards?.includes(2)) {
            return this.evaluateDeucesWildHand(hand);
        } else {
            return this.evaluateStandardHand(hand);
        }
    }
    
    private evaluateStandardHand(hand: Card[]): HandResult {
        const ranks = hand.map(card => card.rank).sort((a, b) => a - b);
        const suits = hand.map(card => card.suit);
        const rankCounts = this.countRanks(ranks);
        
        const isFlush = suits.every(suit => suit === suits[0]);
        const isStraight = this.isStraight(ranks);
        const isRoyal = isStraight && ranks[0] === Rank.TEN && ranks[4] === Rank.ACE;

        // Check for each hand type (highest to lowest)
        if (isRoyal && isFlush) {
            return this.createResult(HandType.ROYAL_FLUSH, 'Royal Flush', { 
                isNatural: true,
                winningCardIndices: [0, 1, 2, 3, 4] // All cards for royal flush
            });
        }
        
        if (isStraight && isFlush) {
            return this.createResult(HandType.STRAIGHT_FLUSH, 'Straight Flush', {
                winningCardIndices: [0, 1, 2, 3, 4] // All cards for straight flush
            });
        }
        
        const quadEntries = Array.from(rankCounts.entries()).filter(([_, count]) => count === 4);
        if (quadEntries.length > 0) {
            const quadRank = quadEntries[0][0];
            const quadIndices = this.findCardIndices(hand, quadRank);
            return this.createBonusQuadResult(quadRank, quadIndices);
        }
        
        const hasThreeOfAKind = Array.from(rankCounts.values()).includes(3);
        const hasPair = Array.from(rankCounts.values()).includes(2);
        if (hasThreeOfAKind && hasPair) {
            return this.createResult(HandType.FULL_HOUSE, 'Full House', {
                winningCardIndices: [0, 1, 2, 3, 4] // All cards for full house
            });
        }
        
        if (isFlush) {
            return this.createResult(HandType.FLUSH, 'Flush', {
                winningCardIndices: [0, 1, 2, 3, 4] // All cards for flush
            });
        }
        
        if (isStraight) {
            return this.createResult(HandType.STRAIGHT, 'Straight', {
                winningCardIndices: [0, 1, 2, 3, 4] // All cards for straight
            });
        }
        
        if (hasThreeOfAKind) {
            const threeRank = Array.from(rankCounts.entries()).find(([_, count]) => count === 3)?.[0];
            if (threeRank) {
                const threeIndices = this.findCardIndices(hand, threeRank);
                return this.createResult(HandType.THREE_OF_A_KIND, 'Three of a Kind', {
                    winningCardIndices: threeIndices
                });
            }
        }
        
        const pairs = Array.from(rankCounts.entries()).filter(([_, count]) => count === 2);
        if (pairs.length === 2) {
            const allPairIndices = pairs.flatMap(([rank]) => this.findCardIndices(hand, rank));
            return this.createResult(HandType.TWO_PAIR, 'Two Pair', {
                winningCardIndices: allPairIndices
            });
        }
        
        if (pairs.length === 1) {
            const pairRank = pairs[0][0];
            const qualifyingRanks = this.variantConfig.specialRules.qualifyingPairs || QUALIFYING_PAIR_RANKS;
            if (qualifyingRanks.includes(pairRank)) {
                const pairIndices = this.findCardIndices(hand, pairRank);
                return this.createResult(HandType.PAIR, `Pair of ${this.getRankName(pairRank)}s`, {
                    winningCardIndices: pairIndices
                });
            }
        }
        
        return this.createResult(HandType.HIGH_CARD, 'High Card');
    }
    
    private evaluateDeucesWildHand(hand: Card[]): HandResult {
        const deuces = hand.filter(card => card.rank === 2);
        const nonDeuces = hand.filter(card => card.rank !== 2);
        const numDeuces = deuces.length;

        // Special case: Four deuces
        if (numDeuces === 4) {
            return this.createResult(HandType.FOUR_DEUCES, 'Four 2s', {
                winningCardIndices: [0, 1, 2, 3, 4]
            });
        }

        // Special case: Five of a kind (only possible with wild cards)
        if (numDeuces > 0) {
            const bestHand = this.findBestWildHand(nonDeuces, numDeuces, hand);
            return bestHand;
        }

        // No deuces - evaluate as natural hand
        return this.evaluateNaturalDeucesWildHand(hand);
    }

    private evaluateNaturalDeucesWildHand(hand: Card[]): HandResult {
        // Similar to standard but with different payouts and no jacks or better requirement
        const ranks = hand.map(card => card.rank).sort((a, b) => a - b);
        const suits = hand.map(card => card.suit);
        const rankCounts = this.countRanks(ranks);

        const isFlush = suits.every(suit => suit === suits[0]);
        const isStraight = this.isStraight(ranks);
        const isRoyal = isStraight && ranks[0] === Rank.TEN && ranks[4] === Rank.ACE;

        // Natural royal flush
        if (isRoyal && isFlush) {
            return this.createResult(HandType.ROYAL_FLUSH, 'Royal Flush', {
                isNatural: true,
                winningCardIndices: [0, 1, 2, 3, 4]
            });
        }

        // Straight flush
        if (isStraight && isFlush) {
            return this.createResult(HandType.STRAIGHT_FLUSH, 'Straight Flush', {
                winningCardIndices: [0, 1, 2, 3, 4]
            });
        }

        // Four of a kind
        const quadEntries = Array.from(rankCounts.entries()).filter(([_, count]) => count === 4);
        if (quadEntries.length > 0) {
            const quadRank = quadEntries[0][0];
            const quadIndices = this.findCardIndices(hand, quadRank);
            return this.createResult(HandType.FOUR_OF_A_KIND, 'Four of a Kind', {
                winningCardIndices: quadIndices
            });
        }

        // Full house
        const hasThreeOfAKind = Array.from(rankCounts.values()).includes(3);
        const hasPair = Array.from(rankCounts.values()).includes(2);
        if (hasThreeOfAKind && hasPair) {
            return this.createResult(HandType.FULL_HOUSE, 'Full House', {
                winningCardIndices: [0, 1, 2, 3, 4]
            });
        }

        // Flush
        if (isFlush) {
            return this.createResult(HandType.FLUSH, 'Flush', {
                winningCardIndices: [0, 1, 2, 3, 4]
            });
        }

        // Straight
        if (isStraight) {
            return this.createResult(HandType.STRAIGHT, 'Straight', {
                winningCardIndices: [0, 1, 2, 3, 4]
            });
        }

        // Three of a kind (minimum paying hand in Deuces Wild)
        if (hasThreeOfAKind) {
            const threeRank = Array.from(rankCounts.entries()).find(([_, count]) => count === 3)?.[0];
            if (threeRank) {
                const threeIndices = this.findCardIndices(hand, threeRank);
                return this.createResult(HandType.THREE_OF_A_KIND, 'Three of a Kind', {
                    winningCardIndices: threeIndices
                });
            }
        }

        // Two pair and single pair don't pay in Deuces Wild
        return this.createResult(HandType.HIGH_CARD, '');
    }

    private findBestWildHand(nonDeuces: Card[], numDeuces: number, fullHand?: Card[]): HandResult {
        // Get rank counts for non-deuce cards
        const ranks = nonDeuces.map(card => card.rank).sort((a, b) => a - b);
        const suits = nonDeuces.map(card => card.suit);
        const rankCounts = this.countRanks(ranks);

        // Check for five of a kind first
        for (const [rank, count] of rankCounts.entries()) {
            if (count + numDeuces === 5) {
                if (fullHand) {
                    const indices = this.findWildAndRankIndices(fullHand, rank);
                    return this.createResult(HandType.FIVE_OF_A_KIND, 'Five of a Kind', {
                        winningCardIndices: indices
                    });
                }
                return this.createResult(HandType.FIVE_OF_A_KIND, 'Five of a Kind', {
                    winningCardIndices: [0, 1, 2, 3, 4]
                });
            }
        }

        // Check for wild royal flush (with deuces)
        const possibleRoyal = this.checkWildRoyal(nonDeuces, numDeuces);
        if (possibleRoyal) {
            return this.createResult(HandType.WILD_ROYAL_FLUSH, 'Royal Flush', {
                winningCardIndices: [0, 1, 2, 3, 4]
            });
        }

        // Check for four of a kind
        for (const [rank, count] of rankCounts.entries()) {
            if (count + numDeuces >= 4) {
                if (fullHand) {
                    const indices = this.findWildAndRankIndices(fullHand, rank);
                    return this.createResult(HandType.FOUR_OF_A_KIND, 'Four of a Kind', {
                        winningCardIndices: indices
                    });
                }
                return this.createResult(HandType.FOUR_OF_A_KIND, 'Four of a Kind', {
                    winningCardIndices: [0, 1, 2, 3, 4]
                });
            }
        }

        // Check for straight flush
        const straightFlush = this.checkWildStraightFlush(nonDeuces, numDeuces);
        if (straightFlush) {
            return this.createResult(HandType.STRAIGHT_FLUSH, 'Straight Flush', {
                winningCardIndices: [0, 1, 2, 3, 4]
            });
        }

        // Check for full house (3 of a kind + pair)
        const fullHouse = this.checkWildFullHouse(rankCounts, numDeuces);
        if (fullHouse) {
            return this.createResult(HandType.FULL_HOUSE, 'Full House', {
                winningCardIndices: [0, 1, 2, 3, 4]
            });
        }

        // Check for flush
        const flush = this.checkWildFlush(suits, numDeuces);
        if (flush) {
            return this.createResult(HandType.FLUSH, 'Flush', {
                winningCardIndices: [0, 1, 2, 3, 4]
            });
        }

        // Check for straight
        const straight = this.checkWildStraight(ranks, numDeuces);
        if (straight) {
            return this.createResult(HandType.STRAIGHT, 'Straight', {
                winningCardIndices: [0, 1, 2, 3, 4]
            });
        }

        // Three of a kind (minimum paying hand)
        for (const [rank, count] of rankCounts.entries()) {
            if (count + numDeuces >= 3) {
                if (fullHand) {
                    const indices = this.findWildAndRankIndices(fullHand, rank);
                    return this.createResult(HandType.THREE_OF_A_KIND, 'Three of a Kind', {
                        winningCardIndices: indices
                    });
                }
                return this.createResult(HandType.THREE_OF_A_KIND, 'Three of a Kind', {
                    winningCardIndices: [0, 1, 2, 3, 4]
                });
            }
        }

        return this.createResult(HandType.HIGH_CARD, '');
    }

    private checkWildRoyal(nonDeuces: Card[], numDeuces: number): boolean {
        // Need T-J-Q-K-A of same suit
        if (nonDeuces.length + numDeuces !== 5) return false;

        // Check if all non-deuces are same suit
        const suits = nonDeuces.map(c => c.suit);
        if (!suits.every(s => s === suits[0])) return false;

        // Check if we can make T-J-Q-K-A with wild cards
        const royalRanks = [10, 11, 12, 13, 14];
        const nonDeuceRanks = nonDeuces.map(c => c.rank);

        let wildsNeeded = 0;
        for (const rank of royalRanks) {
            if (!nonDeuceRanks.includes(rank)) {
                wildsNeeded++;
            }
        }

        return wildsNeeded <= numDeuces && wildsNeeded > 0; // Must use at least one wild
    }

    private checkWildStraightFlush(nonDeuces: Card[], numDeuces: number): boolean {
        // Check if we can make a straight flush with wild cards
        if (nonDeuces.length === 0) return numDeuces === 5;

        // All non-deuces must be same suit
        const suits = nonDeuces.map(c => c.suit);
        if (!suits.every(s => s === suits[0])) return false;

        // Check if we can fill gaps to make a straight
        const ranks = nonDeuces.map(c => c.rank).sort((a, b) => a - b);

        // Try different straight possibilities
        for (let start = Math.max(2, ranks[0] - numDeuces); start <= Math.min(10, ranks[ranks.length - 1]); start++) {
            let wildsUsed = 0;
            let possible = true;

            for (let i = 0; i < 5; i++) {
                const targetRank = start + i === 1 ? 14 : start + i; // Handle ace
                if (!ranks.includes(targetRank)) {
                    wildsUsed++;
                    if (wildsUsed > numDeuces) {
                        possible = false;
                        break;
                    }
                }
            }

            if (possible && wildsUsed <= numDeuces) {
                return true;
            }
        }

        return false;
    }

    private checkWildFullHouse(rankCounts: Map<Rank, number>, numDeuces: number): boolean {
        const pairs = Array.from(rankCounts.entries()).filter(([_, count]) => count === 2);
        const trips = Array.from(rankCounts.entries()).filter(([_, count]) => count === 3);

        // Natural full house
        if (trips.length === 1 && pairs.length === 1) return true;

        // Three of a kind + wild makes full house
        if (trips.length === 1 && numDeuces >= 1) return true;

        // Two pairs + wild makes full house
        if (pairs.length === 2 && numDeuces >= 1) return true;

        // Pair + 2 wilds makes full house
        if (pairs.length === 1 && numDeuces >= 2) return true;

        // 3 wilds can always make full house with 2 different cards
        if (numDeuces >= 3 && rankCounts.size >= 2) return true;

        return false;
    }

    private checkWildFlush(suits: Suit[], numDeuces: number): boolean {
        const suitCounts = new Map<Suit, number>();
        for (const suit of suits) {
            suitCounts.set(suit, (suitCounts.get(suit) || 0) + 1);
        }

        for (const count of suitCounts.values()) {
            if (count + numDeuces >= 5) return true;
        }

        return false;
    }

    private checkWildStraight(ranks: Rank[], numDeuces: number): boolean {
        if (ranks.length === 0) return numDeuces === 5;

        // Try different straight possibilities
        for (let start = Math.max(2, ranks[0] - numDeuces); start <= Math.min(10, ranks[ranks.length - 1]); start++) {
            let wildsUsed = 0;
            let possible = true;

            for (let i = 0; i < 5; i++) {
                const targetRank = start + i === 1 ? 14 : start + i; // Handle ace-low straight
                if (!ranks.includes(targetRank)) {
                    wildsUsed++;
                    if (wildsUsed > numDeuces) {
                        possible = false;
                        break;
                    }
                }
            }

            if (possible && wildsUsed <= numDeuces) {
                return true;
            }
        }

        // Check wheel (A-2-3-4-5) - but 2s are wild, so check A-3-4-5
        if (ranks.includes(14) && ranks.includes(3) && ranks.includes(4) && ranks.includes(5)) {
            return numDeuces >= 1; // Need at least one wild for the 2
        }

        return false;
    }
    
    private createBonusQuadResult(quadRank: Rank, winningCardIndices: number[]): HandResult {
        const bonusQuads = this.variantConfig.specialRules.bonusQuads;
        if (bonusQuads && bonusQuads[quadRank]) {
            const payout = bonusQuads[quadRank][this.state.bet - 1] || 0;
            return {
                type: HandType.FOUR_OF_A_KIND,
                payout,
                description: `Four ${this.getRankName(quadRank)}s`,
                quadRank,
                winningCardIndices
            };
        }
        
        return this.createResult(HandType.FOUR_OF_A_KIND, 'Four of a Kind', { quadRank, winningCardIndices });
    }

    private findCardIndices(hand: Card[], rank: Rank): number[] {
        const indices: number[] = [];
        hand.forEach((card, index) => {
            if (card.rank === rank) {
                indices.push(index);
            }
        });
        return indices;
    }

    private findWildAndRankIndices(hand: Card[], targetRank: Rank, numNeeded: number): number[] {
        // Find indices of target rank cards and deuces (wilds)
        const indices: number[] = [];
        const deuceIndices: number[] = [];

        hand.forEach((card, index) => {
            if (card.rank === targetRank) {
                indices.push(index);
            } else if (card.rank === 2) {
                deuceIndices.push(index);
            }
        });

        // Add enough deuces to make up the needed number
        const deucesNeeded = Math.min(numNeeded - indices.length, deuceIndices.length);
        for (let i = 0; i < deucesNeeded; i++) {
            indices.push(deuceIndices[i]);
        }

        return indices;
    }

    private countRanks(ranks: Rank[]): Map<Rank, number> {
        const counts = new Map<Rank, number>();
        for (const rank of ranks) {
            counts.set(rank, (counts.get(rank) || 0) + 1);
        }
        return counts;
    }

    private isStraight(ranks: Rank[]): boolean {
        // Check for A-2-3-4-5 straight (wheel)
        if (ranks[0] === 2 && ranks[1] === 3 && ranks[2] === 4 && ranks[3] === 5 && ranks[4] === 14) {
            return true;
        }
        
        // Check for regular straight
        for (let i = 1; i < ranks.length; i++) {
            if (ranks[i] !== ranks[i - 1] + 1) {
                return false;
            }
        }
        return true;
    }

    private createResult(type: HandType, description: string, options: { isNatural?: boolean, quadRank?: Rank, winningCardIndices?: number[] } = {}): HandResult {
        const payoutTable = this.config.paytable[type];
        const payout = payoutTable[this.state.bet - 1] || 0;
        
        return {
            type,
            payout,
            description,
            ...options
        };
    }

    private getRankName(rank: Rank): string {
        switch (rank) {
            case Rank.ACE: return 'Ace';
            case Rank.KING: return 'King';
            case Rank.QUEEN: return 'Queen';
            case Rank.JACK: return 'Jack';
            default: return rank.toString();
        }
    }

    public getState(): VideoPokerState {
        return { ...this.state };
    }

    public setBalance(balance: number): void {
        this.state.balance = balance;
    }

    private syncBalance(won: number, lost: number): void {
        const userManager = UserManager.getInstance();
        userManager.updateBalance(this.state.balance, won, lost).catch(error => {
            console.warn('Failed to sync balance with server:', error);
        });
    }

    public getConfig(): VideoPokerConfig {
        return { ...this.config };
    }

    public updateVariantConfig(): void {
        // Reload variant configuration (e.g., when switching variants)
        this.variantConfig = getCurrentVariant();
        this.config.paytable = this.variantConfig.paytable;
    }
}